import CATEGORY_KEYS_DATA from './category-data.json';
import {
  Utensils,
  ShoppingCart,
  Car,
  Plane,
  Hotel,
  Heart,
  ShoppingBag,
  Film,
  Monitor,
  Briefcase,
  BookOpen,
  Scissors,
  Home,
  HelpCircle,
  Fuel,
  HandHeart,
  CalendarRange,
  Navigation,
  Zap,
  Package,
  Globe,
  FileText,
} from 'lucide-react';

// IRS standard mileage rate (2026)
export const IRS_MILEAGE_RATE = 0.725;

export const CURRENCIES = [
  { code: "USD", symbol: "USD", name: "US Dollar" },
  { code: "EUR", symbol: "EUR", name: "Euro" },
  { code: "GBP", symbol: "GBP", name: "British Pound" },
  { code: "JPY", symbol: "JPY", name: "Japanese Yen" },
  { code: "CAD", symbol: "CAD", name: "Canadian Dollar" },
  { code: "AUD", symbol: "AUD", name: "Australian Dollar" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
  { code: "CNY", symbol: "CNY", name: "Chinese Yuan" },
  { code: "INR", symbol: "INR", name: "Indian Rupee" },
  { code: "MXN", symbol: "MXN", name: "Mexican Peso" },
  { code: "BRL", symbol: "BRL", name: "Brazilian Real" },
  { code: "SGD", symbol: "SGD", name: "Singapore Dollar" },
  { code: "VND", symbol: "VND", name: "Vietnamese Dong" },
  { code: "KRW", symbol: "KRW", name: "Korean Won" },
];

export const CATEGORY_KEYS = CATEGORY_KEYS_DATA;

export const CATEGORY_KEY_BY_LABEL = Object.fromEntries(
  Object.entries(CATEGORY_KEYS).map(([k, v]) => [v, k])
);

export const CATEGORIES = Object.values(CATEGORY_KEYS);

// IRS Schedule C / common deduction categories
export const IRS_CATEGORIES = [
  "Advertising",
  "Car & Truck Expenses",
  "Charitable Contributions",
  "Commissions & Fees",
  "Contract Labor",
  "Depreciation, Amortization & Capital Expenses",
  "Education & Training",
  "Home Office",
  "Insurance",
  "Interest Expense",
  "Legal & Professional Services",
  "Medical & Dental",
  "Meals & Entertainment (50%)",
  "Non-deductible (Personal)",
  "Office Expenses",

  "Rent or Lease",
  "Repairs & Maintenance",
  "Schedule D — Capital Losses",
  "Software & Subscriptions",
  "Taxes & Licenses",
  "Travel",
  "Uncategorized",
  "Utilities",
  "Wages & Salaries",

  "Other Business Expenses",
  "Other Expenses",
];

// Default IRS category for each built-in expense category
export const CATEGORY_TAX_MAP = {
  food_dining:         "Meals & Entertainment (50%)",
  groceries:           "Other Expenses",
  gas_fuel:            "Car & Truck Expenses",
  automobile:          "Car & Truck Expenses",
  vehicle_charging:    "Car & Truck Expenses",
  transportation:      "Car & Truck Expenses",
  mileage:             "Car & Truck Expenses",
  hotel_lodging:       "Travel",
  travel:              "Travel",
  healthcare:          "Medical & Dental",
  shopping:            "Non-deductible (Personal)",
  business_gifts:      "Other Expenses",
  entertainment:       "Meals & Entertainment (50%)",
  technology:          "Office Expenses",
  office_business:     "Office Expenses",
  education:           "Education & Training",
  personal_care:       "Non-deductible (Personal)",
  home_garden:         "Non-deductible (Personal)",
  donations:           "Charitable Contributions",
  other:               "Uncategorized",
  per_diem:            "Travel",
  taxes:               "Taxes & Licenses",
  software:            "Software & Subscriptions",
  online_services:     "Software & Subscriptions",
  saas_subscriptions:  "Software & Subscriptions",
  interest_expense:    "Interest Expense",
  bank_fees:           "Other Expenses",
  professional_legal:  "Legal & Professional Services",
  capital_losses_st:   "Schedule D — Capital Losses",
  capital_losses_lt:   "Schedule D — Capital Losses",
};

export const INCOME_CAT_KEYS = {
  consulting:              'Consulting / Professional Fees',
  product_sales:           'Product Sales',
  services_rendered:       'Services Rendered',
  rental_income:           'Rental Income',
  interest_dividends:      'Interest & Dividends',
  taxable_interest:        'Taxable Interest',
  tax_exempt_interest:     'Tax-Exempt Interest',
  taxable_dividends:       'Taxable Dividends',
  tax_exempt_dividends:    'Tax-Exempt Dividends',
  capital_gains_st:        'Capital Gains (Short-term)',
  capital_gains_lt:        'Capital Gains (Long-term)',
  grants_contracts:        'Grants & Contracts',
  employment_income:       'Employment Income',
  refunds_reimbursements:  'Refunds & Reimbursements',
  other_income:            'Other Income',
};

export const INCOME_CAT_KEY_BY_LABEL = Object.fromEntries(
  Object.entries(INCOME_CAT_KEYS).map(([k, v]) => [v, k])
);

export const INCOME_CATEGORIES = Object.values(INCOME_CAT_KEYS);

export const IRS_INCOME_CATEGORIES = [
  '1099-NEC — Freelance / Self-Employment',
  'Schedule C — Business Income',
  '1099-K — Platform / Payment Income',
  'Schedule D — Capital Gains',
  'Schedule E — Rental Income',
  '1099-INT / 1099-DIV — Investment Income',
  '1040 Line 2a — Tax-Exempt Interest & Dividends',
  '1099-B — Brokerage Income',
  'W-2 — Employment Wages',
  'Refund / Reimbursement (non-taxable)',
  'Other Income',
];

export const INCOME_CATEGORY_TAX_MAP = {
  consulting:             '1099-NEC — Freelance / Self-Employment',
  product_sales:          'Schedule C — Business Income',
  services_rendered:      'Schedule C — Business Income',
  rental_income:          'Schedule E — Rental Income',
  interest_dividends:     '1099-INT / 1099-DIV — Investment Income',
  taxable_interest:       '1099-INT / 1099-DIV — Investment Income',
  taxable_dividends:      '1099-INT / 1099-DIV — Investment Income',
  tax_exempt_interest:    '1040 Line 2a — Tax-Exempt Interest & Dividends',
  tax_exempt_dividends:   '1040 Line 2a — Tax-Exempt Interest & Dividends',
  capital_gains_st:       'Schedule D — Capital Gains',
  capital_gains_lt:       'Schedule D — Capital Gains',
  grants_contracts:       'Schedule C — Business Income',
  employment_income:      'W-2 — Employment Wages',
  refunds_reimbursements: 'Refund / Reimbursement (non-taxable)',
  other_income:           'Other Income',
};

export const BIZ_CLASS = {
  business_expense:         { label: 'Biz Expense',           color: '#f35858', bg: '#FEF2F2' },
  business_expense_refund:  { label: 'Biz Expense Refund',    color: '#15803d', bg: '#F0FDF4' },
  business_income:          { label: 'Biz Income',            color: '#15803d', bg: '#F0FDF4' },
  personal_expense:         { label: 'Personal Expense',      color: '#6B7280', bg: '#DEDEDE' },
  personal_expense_refund:  { label: 'Personal Expense Refund', color: '#0891b2', bg: '#ECFEFF' },
  personal_income:          { label: 'Personal Income',       color: '#0891b2', bg: '#ECFEFF' },
  transfer:                 { label: 'Transfer',              color: '#9333ea', bg: '#FAF5FF' },
  owner_reimbursement:      { label: 'Owner Reimb.',          color: '#7c3aed', bg: '#F5F3FF' },
  unclassified_credit:      { label: "Unclassif'd Credit",    color: '#0369a1', bg: '#EFF6FF' },
  unclassified_debit:       { label: "Unclassified Expense",  color: '#92400e', bg: '#FFFBEB' },
};

export const CLASS_SELECT_KEYS = [
  'business_expense', 'business_expense_refund', 'business_income',
  'personal_expense', 'personal_expense_refund', 'personal_income',
  'transfer', 'owner_reimbursement', 'unclassified_credit', 'unclassified_debit',
];

// Refund classifications — credits that net against an expense category instead of being income.
export const EXPENSE_REFUND_CLASS_KEYS = new Set(['business_expense_refund', 'personal_expense_refund']);

// Map old → new classification keys (used by client/server migration paths and back-compat shims).
// `personal` was renamed to `personal_expense` for symmetry with the other classifications.
export const LEGACY_CLASSIFICATION_MAP = {
  personal: 'personal_expense',
};

// Resolve a (possibly legacy) classification key to the current key.
// Folds an isRefund flag into the appropriate refund classification, applies the
// personal → personal_expense rename, and converts the pre-feature workaround
// (credit transactions classified as expense-side) into the refund classification.
export function resolveClassification(cls, isRefund, transactionDirection) {
  if (isRefund) {
    if (cls === 'business_income') return 'business_expense_refund';
    if (cls === 'personal_income')  return 'personal_expense_refund';
  }
  const renamed = LEGACY_CLASSIFICATION_MAP[cls] || cls;
  if (transactionDirection === 'credit') {
    if (renamed === 'business_expense')  return 'business_expense_refund';
    if (renamed === 'personal_expense')  return 'personal_expense_refund';
  }
  return renamed;
}

export const REPORT_TYPES = {
  expense:     'expense',
  credit_card: 'credit_card',
  bank:        'bank',
  brokerage:   'brokerage',
};

export const REPORT_TYPE_LABELS = {
  expense:     'Expense Report',
  credit_card: 'Credit Card',
  bank:        'Bank Account',
  brokerage:   'Brokerage Account',
};

export const CATEGORY_ICONS = {
  food_dining:       Utensils,
  groceries:         ShoppingCart,
  gas_fuel:          Fuel,
  transportation:    Car,
  mileage:           Navigation,
  hotel_lodging:     Hotel,
  travel:            Plane,
  healthcare:        Heart,
  shopping:          ShoppingBag,
  entertainment:     Film,
  technology:        Monitor,
  office_business:   Briefcase,
  education:         BookOpen,
  personal_care:     Scissors,
  home_garden:       Home,
  donations:         HandHeart,
  other:             HelpCircle,
  per_diem:          CalendarRange,
  software:          Package,
  online_services:   Globe,
  saas_subscriptions:Package,
  vehicle_charging:  Zap,
  taxes:             FileText,
};
